import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

interface ChartData {
  monthlyApp: { month: string; basvuru: number; aktif: number }[];
  branchDemand: { name: string; value: number }[];
  monthlyRevenue: { month: string; gelir: number; hakedis: number }[];
  teacherPerf: { teacher: string; students: number; lessons: number }[];
  conversion: { name: string; value: number; color: string }[];
}

const monthNames: Record<string, string> = {
  '01':'Oca','02':'Şub','03':'Mar','04':'Nis','05':'May','06':'Haz',
  '07':'Tem','08':'Ağu','09':'Eyl','10':'Eki','11':'Kas','12':'Ara',
};

const BRANCH_COLORS = ['#1a2f5a', '#3b5bdb', '#7c6af5', '#10b981', '#f59e0b', '#94a3b8', '#ef4444', '#6366f1'];

const CONVERSION_COLORS: Record<string, string> = {
  'Aktif Derse Döndü': '#10b981',
  'Kararsız': '#f59e0b',
  'Devam Etmeyecek': '#ef4444',
  'Tekrar Aranacak': '#3b5bdb',
  'Planlandı': '#94a3b8',
};

function ChartCard({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 p-5 ${className}`}>
      <p className="text-sm font-semibold text-gray-800 mb-4">{title}</p>
      {children}
    </div>
  );
}

export default function DashboardCharts() {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      const today = new Date().toISOString().split('T')[0];
      const sixMonthsAgo = new Date(Date.now() - 180 * 86400000).toISOString().split('T')[0];

      const [
        { data: apps },
        { data: students },
        { data: payments },
        { data: teachers },
        { data: lessons },
        { data: trials },
      ] = await Promise.all([
        supabase.from('egtpanel_basvurular').select('basvuru_tarihi').gte('basvuru_tarihi', sixMonthsAgo),
        supabase.from('egtpanel_ogrenciler').select('olusturma_tarihi, aktif').gte('olusturma_tarihi', sixMonthsAgo),
        supabase.from('egtpanel_odemeler').select('dekont_tarihi, tutar, ogretmen_payi').eq('yonetici_onay', true).gte('dekont_tarihi', sixMonthsAgo),
        supabase.from('egtpanel_ogretmenler').select('id, ad_soyad').eq('aktif', true),
        supabase.from('egtpanel_aktif_dersler').select('ders_tarihi, ogretmen_id').gte('ders_tarihi', sixMonthsAgo),
        supabase.from('egtpanel_deneme_dersleri').select('sonuc').gte('olusturma_tarihi', sixMonthsAgo),
      ]);

      // Aylık başvuru + aktif öğrenci
      const monthlyMap: Record<string, { basvuru: number; aktif: number }> = {};
      const months: string[] = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        months.push(key);
        monthlyMap[key] = { basvuru: 0, aktif: 0 };
      }
      (apps || []).forEach(a => {
        const m = (a.basvuru_tarihi ?? '').slice(0, 7);
        if (monthlyMap[m]) monthlyMap[m].basvuru++;
      });
      (students || []).forEach(s => {
        const m = (s.olusturma_tarihi ?? '').slice(0, 7);
        if (monthlyMap[m] && s.aktif) monthlyMap[m].aktif++;
      });
      const monthlyApp = months.map(m => ({
        month: monthNames[m.slice(5)] || m,
        basvuru: monthlyMap[m].basvuru,
        aktif: monthlyMap[m].aktif,
      }));

      // Branş talebi (basvurular + ogrenciler)
      const [{ data: appBranches }, { data: stuBranches }] = await Promise.all([
        supabase.from('egtpanel_basvurular').select('brans'),
        supabase.from('egtpanel_ogrenciler').select('branslar'),
      ]);
      const branchCounts: Record<string, number> = {};
      (appBranches || []).forEach(b => {
        const br = (b as { brans?: string }).brans;
        if (br) branchCounts[br] = (branchCounts[br] || 0) + 1;
      });
      (stuBranches || []).forEach(s => {
        const arr = (s as { branslar?: string[] }).branslar;
        if (Array.isArray(arr)) arr.forEach(br => {
          branchCounts[br] = (branchCounts[br] || 0) + 1;
        });
      });
      const branchDemand = Object.entries(branchCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([name, value], i) => ({ name, value, color: BRANCH_COLORS[i % BRANCH_COLORS.length] }));

      // Aylık gelir vs hak ediş
      const revenueMap: Record<string, { gelir: number; hakedis: number }> = {};
      months.forEach(m => revenueMap[m] = { gelir: 0, hakedis: 0 });
      (payments || []).forEach(p => {
        const m = (p.dekont_tarihi ?? '').slice(0, 7);
        if (revenueMap[m]) {
          revenueMap[m].gelir += Number(p.tutar || 0);
          revenueMap[m].hakedis += Number(p.ogretmen_payi || 0);
        }
      });
      const monthlyRevenue = months.map(m => ({
        month: monthNames[m.slice(5)] || m,
        gelir: revenueMap[m].gelir,
        hakedis: revenueMap[m].hakedis,
      }));

      // Öğretmen performansı
      const teacherPerf = await Promise.all((teachers || []).map(async (t: { id: string; ad_soyad: string }) => {
        const { count: stuCount } = await supabase.from('egtpanel_ogrenciler').select('id', { count: 'exact', head: true }).eq('atanan_ogretmen_id', t.id).eq('aktif', true);
        const lessonCount = (lessons || []).filter(l => l.ogretmen_id === t.id).length;
        return { teacher: t.ad_soyad.split(' ')[0] || t.ad_soyad, students: stuCount || 0, lessons: lessonCount };
      }));
      const teacherPerfSorted = teacherPerf.sort((a, b) => b.lessons - a.lessons).slice(0, 5);

      // Dönüşüm oranı
      const conversionCounts: Record<string, number> = {};
      (trials || []).forEach(t => {
        const s = (t as { sonuc?: string }).sonuc || 'Planlandı';
        conversionCounts[s] = (conversionCounts[s] || 0) + 1;
      });
      const conversion = Object.entries(conversionCounts).map(([name, value]) => ({
        name,
        value,
        color: CONVERSION_COLORS[name] || '#94a3b8',
      }));

      setData({ monthlyApp, branchDemand, monthlyRevenue, teacherPerf: teacherPerfSorted, conversion });
      setLoading(false);
    };
    fetchAll();
  }, []);

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 h-64 flex items-center justify-center">
            <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
      {/* Aylık Başvuru & Aktif Öğrenci Trendi */}
      <ChartCard title="Aylık Başvuru & Aktif Öğrenci Trendi" className="xl:col-span-2">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data.monthlyApp}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <Tooltip />
            <Legend formatter={(value) => <span style={{ fontSize: 12, color: '#64748b' }}>{value === 'basvuru' ? 'Başvuru' : 'Aktif Öğrenci'}</span>} />
            <Line type="monotone" dataKey="basvuru" name="Başvuru" stroke="#3b5bdb" strokeWidth={2.5} dot={{ r: 4, fill: '#3b5bdb' }} activeDot={{ r: 6 }} />
            <Line type="monotone" dataKey="aktif" name="Aktif Öğrenci" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Branş Dağılımı */}
      <ChartCard title="Branşlara Göre Talep Dağılımı">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={data.branchDemand} cx="50%" cy="45%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
              {data.branchDemand.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => [`${value}`, '']} contentStyle={{ borderRadius: 12, border: '1px solid #f1f5f9', fontSize: 12 }} />
            <Legend iconType="circle" iconSize={8} formatter={(value) => <span style={{ fontSize: 11, color: '#64748b' }}>{value}</span>} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Gelir vs Hak Ediş */}
      <ChartCard title="Gelir / Öğretmen Hak Edişi Karşılaştırması" className="xl:col-span-2">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.monthlyRevenue} barSize={24} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}K`} />
            <Tooltip />
            <Legend formatter={(value) => <span style={{ fontSize: 12, color: '#64748b' }}>{value === 'gelir' ? 'Toplam Gelir' : 'Hak Ediş'}</span>} />
            <Bar dataKey="gelir" name="gelir" fill="#3b5bdb" radius={[4, 4, 0, 0]} />
            <Bar dataKey="hakedis" name="hakediş" fill="#7c6af5" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Dönüşüm Oranı */}
      <ChartCard title="Deneme Ders Dönüşüm Oranı">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data.conversion} cx="50%" cy="45%" outerRadius={80} paddingAngle={3} dataKey="value">
              {data.conversion.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #f1f5f9', fontSize: 12 }} />
            <Legend iconType="circle" iconSize={8} formatter={(value) => <span style={{ fontSize: 11, color: '#64748b' }}>{value}</span>} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Öğretmen Performansı */}
      <ChartCard title="Öğretmen Bazlı Aktif Öğrenci Sayısı" className="lg:col-span-2 xl:col-span-3">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data.teacherPerf} barSize={28} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="teacher" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} width={65} />
            <Tooltip />
            <Bar dataKey="students" name="Aktif Öğrenci" fill="#1a2f5a" radius={[0, 4, 4, 0]} />
            <Bar dataKey="lessons" name="Aylık Ders" fill="#7ab3f5" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}